type CacheEntry = {
  signedUrl: string;
  expiresAt?: number; // epoch ms
};

const STORAGE_KEY = 'imageSignedUrlCache_v1';

const cache: Record<string, CacheEntry> = {};

const loadFromStorage = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, CacheEntry>;
    Object.assign(cache, parsed);
  } catch (err) {
    // ignore
    console.warn('Failed to load image cache from sessionStorage', err);
  }
};

const persistToStorage = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to persist image cache to sessionStorage', err);
  }
};

// Initialize from sessionStorage
loadFromStorage();

export const getEntry = (originalUrl: string): CacheEntry | undefined => {
  return cache[originalUrl];
};

export const getSignedUrlFromCache = (originalUrl: string): string | undefined => {
  const entry = cache[originalUrl];
  if (!entry) return undefined;
  
  // Check if the cached signed URL has expired
  if (entry.expiresAt && Date.now() >= entry.expiresAt) {
    // Expired, remove from cache
    removeEntry(originalUrl);
    return undefined;
  }
  
  return entry.signedUrl;
};

export const setSignedUrlInCache = (originalUrl: string, signedUrl: string, expiresAt?: number) => {
  if (!originalUrl || !signedUrl) return;
  cache[originalUrl] = { signedUrl, expiresAt };
  persistToStorage();
};

export const getAllEntries = (): Record<string, CacheEntry> => {
  return { ...cache };
};

export const removeEntry = (originalUrl: string) => {
  delete cache[originalUrl];
  persistToStorage();
};

export const clearImageCache = () => {
  Object.keys(cache).forEach(k => delete cache[k]);
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (err) { /* ignore */ }
};

export default {
  getEntry,
  getSignedUrlFromCache,
  setSignedUrlInCache,
  getAllEntries,
  removeEntry,
  clearImageCache
};
